/**
 * Referral / affiliate API client.
 *
 * Each user gets a unique referral code. When a referred user signs up
 * and converts to paid, the referrer earns credit.
 */
import { apiGet, apiPost } from "./client.ts";

export interface ReferralStats {
  referralCode: string;
  referralUrl: string;
  totalClicks: number;
  totalSignups: number;
  totalConversions: number;
  totalEarned: number; // in dollars or credits
  referrals: ReferralEntry[];
}

export interface ReferralEntry {
  id: string;
  referredEmail: string; // masked: j***@example.com
  status: "signed_up" | "converted" | "churned";
  reward: number;
  createdAt: string;
  convertedAt: string | null;
}

export const referralsApi = {
  /** Get current user's referral stats and history. */
  stats: () => apiGet<ReferralStats>("/referrals/stats"),

  /** Generate or retrieve the user's referral code. */
  getCode: () => apiGet<{ code: string; url: string }>("/referrals/code"),

  /** Record that a referred user signed up (called server-side, but client can trigger). */
  recordSignup: (referralCode: string) =>
    apiPost<{ ok: boolean }>("/referrals/record", { referralCode }),

  /** Admin: list all referrals across all users. */
  adminList: (params: { limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.offset) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return apiGet<{
      referrals: Array<ReferralEntry & { referrerEmail: string }>;
      pagination: { limit: number; offset: number; total: number };
    }>(`/admin/referrals${q ? `?${q}` : ""}`);
  },
};
